import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LettuceLogo from "../../../images/logo.png";
import AccountAvatarMenu from "./AccountAvatarMenu";

export function hasSameOriginDocumentReferrer() {
  if (
    typeof document === "undefined" ||
    typeof window === "undefined" ||
    !document.referrer ||
    window.history.length <= 1
  ) {
    return false;
  }

  try {
    return new URL(document.referrer).origin === window.location.origin;
  } catch {
    return false;
  }
}

export function shouldShowPageBackButton(locationState) {
  return (
    locationState?.fromLettuceTalk === true || hasSameOriginDocumentReferrer()
  );
}

function getUsablePartnerLogoUrl(partnerBranding) {
  const logoUrl =
    typeof partnerBranding?.logoUrl === "string"
      ? partnerBranding.logoUrl.trim()
      : "";

  if (
    logoUrl.startsWith("/") ||
    /^https?:\/\//i.test(logoUrl)
  ) {
    return logoUrl;
  }

  return "";
}

function getPartnerLogoAlt(partnerBranding) {
  return typeof partnerBranding?.logoAlt === "string" &&
    partnerBranding.logoAlt.trim()
    ? partnerBranding.logoAlt.trim()
    : "Partner logo";
}

export function PartnerLogo({
  partnerBranding,
  className = "",
  size = "default",
  onLogoError = () => {},
}) {
  const [loadedLogoUrl, setLoadedLogoUrl] = useState("");
  const partnerLogoUrl = getUsablePartnerLogoUrl(partnerBranding);
  const logoLoaded = loadedLogoUrl === partnerLogoUrl;

  if (
    !partnerBranding ||
    typeof partnerBranding !== "object" ||
    !partnerLogoUrl
  ) {
    return null;
  }

  return (
    <span
      className={`relative inline-flex items-center justify-center overflow-hidden rounded-full border border-white/70 bg-[linear-gradient(180deg,#fffdf8_0%,#f7f1e5_56%,#eee5d4_100%)] shadow-[0_3px_14px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.95),inset_0_-1px_0_rgba(171,145,57,0.16)] backdrop-blur-md ${!logoLoaded ? "invisible" : ""} ${size === "large" ? "max-w-[180px] px-2 py-1.5 sm:max-w-[220px] sm:px-3 sm:py-2" : size === "header" ? "min-h-[44px] max-w-[120px] px-1.5 py-0.5 sm:min-h-[50px] sm:max-w-[130px] sm:px-2 sm:py-1" : "max-w-[120px] px-1.5 py-0.5 sm:max-w-[130px] sm:px-2 sm:py-1"} ${className}`}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-[18%] top-0 h-px bg-white/90 blur-[1px]"
      />
      <img
        src={partnerLogoUrl}
        alt={getPartnerLogoAlt(partnerBranding)}
        aria-hidden={!logoLoaded}
        onLoad={() => setLoadedLogoUrl(partnerLogoUrl)}
        onError={() => onLogoError(partnerLogoUrl)}
        className={`relative z-[1] object-contain drop-shadow-[0_1px_1px_rgba(255,255,255,0.35)] ${size === "large" ? "h-8 w-[140px] sm:h-10 sm:w-[180px]" : size === "header" ? "h-9 w-[110px] sm:h-10 sm:w-[120px]" : "h-5 w-[90px] sm:h-7 sm:w-[110px]"}`}
      />
    </span>
  );
}

function EmpathyPageHeader({
  title,
  partnerBranding,
  authUser,
  className = "",
  onBack,
  showBackButton,
  showAccountMenu = true,
  signOutRedirectTo = "",
  accountSessionDraft,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [failedPartnerLogoUrl, setFailedPartnerLogoUrl] = useState("");
  const canGoBack =
    showBackButton ?? shouldShowPageBackButton(location.state);
  const partnerLogoUrl = getUsablePartnerLogoUrl(partnerBranding);
  const showPartnerLogo =
    Boolean(partnerBranding) &&
    typeof partnerBranding === "object" &&
    Boolean(partnerLogoUrl) &&
    failedPartnerLogoUrl !== partnerLogoUrl;

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    navigate(-1);
  };

  return (
    <header
      className={`relative z-50 grid grid-cols-[44px_minmax(0,1fr)_44px] items-center gap-2 sm:grid-cols-[50px_minmax(0,1fr)_50px] ${className}`}
    >
      {canGoBack ? (
        <button
          type="button"
          onClick={handleBack}
          aria-label="Back"
          className="flex h-11 w-11 items-center justify-center rounded-full border border-[#f4d94e]/24 bg-[#f4d94e]/10 text-[#fff6b8] shadow-[0_0_26px_rgba(244,217,78,0.24),0_12px_28px_rgba(0,0,0,0.22),inset_0_1px_0_rgba(244,217,78,0.16)] backdrop-blur-xl transition hover:bg-[#f4d94e]/16 active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f4d94e] sm:h-[50px] sm:w-[50px]"
        >
          <svg
            aria-hidden="true"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 20 20"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 4 6 10l6 6" />
            <path d="M7 10h9" />
          </svg>
        </button>
      ) : (
        <Link
          to="/refine"
          aria-label="Create a card"
          className="relative flex h-11 w-11 items-center justify-center rounded-full bg-[#f4d94e]/10 shadow-[0_0_26px_rgba(244,217,78,0.24),0_12px_28px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(244,217,78,0.16)] backdrop-blur-xl transition hover:bg-[#f4d94e]/16 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f4d94e] sm:h-[50px] sm:w-[50px]"
        >
          <span className="pointer-events-none absolute inset-0 rounded-full bg-[radial-gradient(circle_at_38%_24%,rgba(244,217,78,0.22),transparent_68%)]" />
          <img
            src={LettuceLogo}
            alt="LettuceTalk"
            className="relative h-9 w-9 object-contain p-1 drop-shadow-[0_8px_14px_rgba(48,118,9,0.2)] sm:h-10 sm:w-10"
          />
        </Link>
      )}

      <div className="flex min-w-0 justify-center">
        <h1
          aria-label={showPartnerLogo ? title : undefined}
          className={
            showPartnerLogo
              ? "flex min-h-10 max-w-full items-center justify-center text-center"
              : "relative inline-flex min-h-[44px] max-w-full items-center justify-center rounded-full bg-[#f4d94e]/10 px-3 py-0 text-center font-sans text-[10px] font-medium uppercase leading-tight tracking-[0.12em] text-[#f4d94e] shadow-[0_12px_36px_rgba(0,0,0,0.22),inset_0_1px_0_rgba(244,217,78,0.16)] drop-shadow-[0_0_16px_rgba(244,217,78,0.22)] backdrop-blur-xl min-[360px]:text-[11px] sm:min-h-[50px] sm:px-5 sm:text-sm sm:tracking-[0.18em]"
          }
        >
          {showPartnerLogo ? (
            <PartnerLogo
              partnerBranding={partnerBranding}
              size="header"
              onLogoError={setFailedPartnerLogoUrl}
            />
          ) : (
            <>
              <span className="pointer-events-none absolute inset-0 rounded-full bg-[radial-gradient(circle_at_50%_0%,rgba(244,217,78,0.16),transparent_70%)]" />
              <span className="relative z-[1]">{title}</span>
            </>
          )}
        </h1>
      </div>

      {showAccountMenu ? (
        <AccountAvatarMenu
          user={authUser}
          accountSessionDraft={accountSessionDraft}
          {...(signOutRedirectTo ? { signOutRedirectTo } : {})}
        />
      ) : (
        <div className="h-11 w-11 sm:h-[50px] sm:w-[50px]" aria-hidden="true" />
      )}
    </header>
  );
}

export default EmpathyPageHeader;
